import { useEffect, useMemo, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { Play, Pause, RotateCcw, Download } from "@/lib/icons";
import { downloadPhaseLabPdf } from "@/lib/phaseLabPdf";

/**
 * Phase-Change & Heating Curve Lab.
 *
 * Three synced views driven by a single scalar `progress` in [0,1] that
 * represents heat added to a fixed mass of substance:
 *   - Heating curve (T vs Q)
 *   - Particle box (~80 molecules, motion + arrangement reflect phase)
 *   - KE/PE bars (KE flat during phase changes, PE rises)
 *
 * No physics engine; everything is closed-form from `progress` so the user
 * can scrub freely.
 */

type Substance = {
  key: string;
  name: string;
  formula: string;
  /** °C */
  meltC: number;
  /** °C */
  boilC: number;
  /** kJ/kg, latent heat of fusion */
  Lf: number;
  /** kJ/kg, latent heat of vaporization */
  Lv: number;
  /** kJ/(kg·K) for solid, liquid, gas */
  cs: number;
  cl: number;
  cg: number;
  startC: number;
  endC: number;
};

const SUBSTANCES: Substance[] = [
  {
    key: "water",
    name: "Water",
    formula: "H2O",
    meltC: 0,
    boilC: 100,
    Lf: 334,
    Lv: 2260,
    cs: 2.09,
    cl: 4.18,
    cg: 2.01,
    startC: -20,
    endC: 140,
  },
  {
    key: "ethanol",
    name: "Ethanol",
    formula: "C2H5OH",
    meltC: -114,
    boilC: 78,
    Lf: 109,
    Lv: 841,
    cs: 1.7,
    cl: 2.44,
    cg: 1.42,
    startC: -130,
    endC: 110,
  },
  {
    key: "lead",
    name: "Lead",
    formula: "Pb",
    meltC: 327,
    boilC: 1749,
    Lf: 23,
    Lv: 858,
    cs: 0.13,
    cl: 0.14,
    cg: 0.13,
    startC: 25,
    endC: 1900,
  },
];

/**
 * Compute Q-segments (kJ/kg) for the heating curve. Returns the cumulative
 * Q at each break point and total Q for normalization.
 */
function segments(s: Substance) {
  const q1 = s.cs * (s.meltC - s.startC); // warm solid
  const q2 = s.Lf; // melt
  const q3 = s.cl * (s.boilC - s.meltC); // warm liquid
  const q4 = s.Lv; // boil
  const q5 = s.cg * (s.endC - s.boilC); // warm gas
  const breaks = [0, q1, q1 + q2, q1 + q2 + q3, q1 + q2 + q3 + q4, q1 + q2 + q3 + q4 + q5];
  return { breaks, total: breaks[5], q1, q2, q3, q4, q5 };
}

type PhaseInfo = {
  /** "solid" | "melting" | "liquid" | "boiling" | "gas" */
  phase: "solid" | "melting" | "liquid" | "boiling" | "gas";
  /** Current temperature in °C. */
  tempC: number;
  /** 0..1 within the current segment. */
  segT: number;
  /** 0..1 fraction liquid (for melting), or fraction gas (for boiling). */
  fraction: number;
};

function phaseAt(s: Substance, q: number): PhaseInfo {
  const seg = segments(s);
  const b = seg.breaks;
  if (q <= b[1]) {
    const t = b[1] === 0 ? 0 : q / b[1];
    return {
      phase: "solid",
      tempC: s.startC + (s.meltC - s.startC) * t,
      segT: t,
      fraction: 0,
    };
  }
  if (q <= b[2]) {
    const t = (q - b[1]) / Math.max(1e-9, b[2] - b[1]);
    return { phase: "melting", tempC: s.meltC, segT: t, fraction: t };
  }
  if (q <= b[3]) {
    const t = (q - b[2]) / Math.max(1e-9, b[3] - b[2]);
    return {
      phase: "liquid",
      tempC: s.meltC + (s.boilC - s.meltC) * t,
      segT: t,
      fraction: 0,
    };
  }
  if (q <= b[4]) {
    const t = (q - b[3]) / Math.max(1e-9, b[4] - b[3]);
    return { phase: "boiling", tempC: s.boilC, segT: t, fraction: t };
  }
  const t = (q - b[4]) / Math.max(1e-9, b[5] - b[4]);
  return {
    phase: "gas",
    tempC: s.boilC + (s.endC - s.boilC) * t,
    segT: t,
    fraction: 1,
  };
}

const PHASE_LABEL: Record<PhaseInfo["phase"], string> = {
  solid: "Solid — particles vibrate in fixed lattice positions.",
  melting: "Melting — added heat breaks lattice bonds. KE (temperature) holds steady; PE rises.",
  liquid: "Liquid — particles slide past each other, still in contact.",
  boiling: "Boiling — added heat overcomes intermolecular attractions. KE flat, PE rises.",
  gas: "Gas — particles fly freely; collisions are rare and elastic.",
};

const PHASE_TOOLTIP: Record<PhaseInfo["phase"], { ke: string; pe: string }> = {
  solid: {
    ke: "KE rises with T — particles vibrate harder around their lattice sites.",
    pe: "PE roughly flat — bonds intact, average separation unchanged.",
  },
  melting: {
    ke: "KE flat — temperature pinned at the melting point.",
    pe: "PE climbs — heat is breaking lattice attractions, not speeding particles up.",
  },
  liquid: {
    ke: "KE rises with T — particles slide and collide faster.",
    pe: "PE creeps up slightly as average particle spacing grows.",
  },
  boiling: {
    ke: "KE flat — temperature pinned at the boiling point.",
    pe: "PE jumps sharply — heat overcomes the intermolecular attractions holding the liquid together.",
  },
  gas: {
    ke: "KE rises rapidly with T — molecules fly faster between rare collisions.",
    pe: "PE high and nearly flat — particles are essentially free of one another.",
  },
};

const N_PARTICLES = 64;

const RATE_OPTIONS: { key: string; label: string; mult: number }[] = [
  { key: "slow", label: "0.5×", mult: 0.5 },
  { key: "normal", label: "1×", mult: 1 },
  { key: "fast", label: "2×", mult: 2 },
  { key: "vfast", label: "4×", mult: 4 },
];

export default function PhaseChangeLab() {
  const [subKey, setSubKey] = useState("water");
  const sub = useMemo(
    () => SUBSTANCES.find((s) => s.key === subKey) ?? SUBSTANCES[0],
    [subKey],
  );
  const [progress, setProgress] = useState(0.05);
  const [playing, setPlaying] = useState(false);
  const [rateMult, setRateMult] = useState(1);
  const [showTip, setShowTip] = useState(false);
  const seg = useMemo(() => segments(sub), [sub]);
  const info = useMemo(() => phaseAt(sub, progress * seg.total), [sub, progress, seg]);

  // Curve canvas
  const curveRef = useRef<HTMLCanvasElement | null>(null);
  const boxRef = useRef<HTMLCanvasElement | null>(null);
  const animRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);

  // Persistent particle state — only velocity scale + connectedness shifts
  // with phase. Positions persist across frames for continuity.
  const particlesRef = useRef<
    { x: number; y: number; vx: number; vy: number; rx: number; ry: number; phase: number }[]
  >([]);
  if (particlesRef.current.length === 0) {
    for (let i = 0; i < N_PARTICLES; i++) {
      particlesRef.current.push({
        x: Math.random(),
        y: Math.random(),
        vx: (Math.random() - 0.5) * 0.001,
        vy: (Math.random() - 0.5) * 0.001,
        rx: 0,
        ry: 0,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  // Draw heating curve
  useEffect(() => {
    const canvas = curveRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const padL = 44;
    const padR = 12;
    const padT = 14;
    const padB = 28;
    const pw = w - padL - padR;
    const ph = h - padT - padB;

    const tMin = sub.startC;
    const tMax = sub.endC;
    const xQ = (q: number) => padL + (q / seg.total) * pw;
    const yT = (t: number) => padT + ph - ((t - tMin) / (tMax - tMin)) * ph;

    // Axes
    ctx.strokeStyle = "hsl(220 13% 30%)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + ph);
    ctx.lineTo(padL + pw, padT + ph);
    ctx.stroke();

    // Reference temperature lines (melt + boil)
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = "hsl(220 13% 25%)";
    [sub.meltC, sub.boilC].forEach((t) => {
      ctx.beginPath();
      ctx.moveTo(padL, yT(t));
      ctx.lineTo(padL + pw, yT(t));
      ctx.stroke();
    });
    ctx.setLineDash([]);

    // Plateau highlight overlay — emphasize the current plateau when scrubbing
    // through melting or boiling, otherwise show both as faint hints.
    const plateauBands: { from: number; to: number; t: number; active: boolean }[] = [
      {
        from: seg.breaks[1],
        to: seg.breaks[2],
        t: sub.meltC,
        active: info.phase === "melting",
      },
      {
        from: seg.breaks[3],
        to: seg.breaks[4],
        t: sub.boilC,
        active: info.phase === "boiling",
      },
    ];
    plateauBands.forEach((b) => {
      const x0 = xQ(b.from);
      const x1 = xQ(b.to);
      const yLine = yT(b.t);
      ctx.fillStyle = b.active ? "hsl(45 95% 60% / 0.18)" : "hsl(45 95% 60% / 0.06)";
      ctx.fillRect(x0, padT, x1 - x0, ph);
      ctx.strokeStyle = b.active ? "hsl(45 95% 60% / 0.9)" : "hsl(45 95% 60% / 0.35)";
      ctx.lineWidth = b.active ? 1.5 : 1;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.moveTo(x0, yLine);
      ctx.lineTo(x1, yLine);
      ctx.stroke();
      ctx.setLineDash([]);
      if (b.active) {
        ctx.fillStyle = "hsl(45 95% 60%)";
        ctx.font = "bold 10px ui-sans-serif, system-ui";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText("plateau", (x0 + x1) / 2, yLine - 4);
      }
    });

    // The curve itself
    ctx.strokeStyle = "hsl(210 90% 60%)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    const pts: [number, number][] = [
      [0, sub.startC],
      [seg.breaks[1], sub.meltC],
      [seg.breaks[2], sub.meltC],
      [seg.breaks[3], sub.boilC],
      [seg.breaks[4], sub.boilC],
      [seg.breaks[5], sub.endC],
    ];
    pts.forEach(([q, t], i) => {
      const x = xQ(q);
      const y = yT(t);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Y-axis labels
    ctx.fillStyle = "hsl(220 13% 60%)";
    ctx.font = "10px ui-sans-serif, system-ui";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    [sub.startC, sub.meltC, sub.boilC, sub.endC].forEach((t) => {
      ctx.fillText(`${Math.round(t)}°`, padL - 6, yT(t));
    });

    // Phase region labels
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    const labels: [string, number][] = [
      ["solid", (seg.breaks[0] + seg.breaks[1]) / 2],
      ["melt", (seg.breaks[1] + seg.breaks[2]) / 2],
      ["liquid", (seg.breaks[2] + seg.breaks[3]) / 2],
      ["boil", (seg.breaks[3] + seg.breaks[4]) / 2],
      ["gas", (seg.breaks[4] + seg.breaks[5]) / 2],
    ];
    ctx.fillStyle = "hsl(220 13% 50%)";
    labels.forEach(([l, q]) => {
      ctx.fillText(l, xQ(q), padT + ph + 16);
    });

    // Marker
    const q = progress * seg.total;
    const mx = xQ(q);
    const my = yT(info.tempC);
    ctx.fillStyle = "hsl(45 95% 60%)";
    ctx.beginPath();
    ctx.arc(mx, my, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "hsl(45 95% 60% / 0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(mx, padT);
    ctx.lineTo(mx, padT + ph);
    ctx.stroke();
  }, [sub, progress, info.tempC, seg]);

  // Particle box animation
  useEffect(() => {
    const canvas = boxRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const tick = (now: number) => {
      const dt = lastTimeRef.current ? Math.min(0.05, (now - lastTimeRef.current) / 1000) : 0.016;
      lastTimeRef.current = now;

      // Speed scales with KE (temperature in K, normalized).
      const tempK = info.tempC + 273.15;
      const refK = sub.boilC + 273.15;
      const speedScale = Math.sqrt(Math.max(0.05, tempK / refK));

      // Phase determines particle behavior.
      const phase = info.phase;
      const liquidFrac =
        phase === "solid"
          ? 0
          : phase === "melting"
            ? info.fraction
            : 1;
      const gasFrac =
        phase === "gas"
          ? 1
          : phase === "boiling"
            ? info.fraction
            : 0;

      ctx.clearRect(0, 0, w, h);

      // Container
      ctx.strokeStyle = "hsl(220 13% 28%)";
      ctx.lineWidth = 1;
      ctx.strokeRect(4, 4, w - 8, h - 8);

      // Lattice anchor positions (8x8 grid)
      const cols = 8;
      const rows = 8;
      const pad = 16;
      const cellW = (w - 2 * pad) / cols;
      const cellH = (h - 2 * pad) / rows;

      const ps = particlesRef.current;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        const col = i % cols;
        const row = Math.floor(i / cols);
        const ax = pad + (col + 0.5) * cellW;
        const ay = pad + (row + 0.5) * cellH;

        // Decide per-particle phase: bottom rows liquefy first, top rows gasify first.
        const liqHere = liquidFrac > 1 - row / rows;
        const gasHere = gasFrac > row / rows;

        let cx: number;
        let cy: number;
        const dtScale = reduced ? 0 : 1;

        if (gasHere) {
          // Free flight - integrate velocity and bounce.
          const v = 70 * speedScale;
          p.x += p.vx * v * dt * dtScale;
          p.y += p.vy * v * dt * dtScale;
          if (p.x < 0.02 || p.x > 0.98) {
            p.vx *= -1;
            p.x = Math.max(0.02, Math.min(0.98, p.x));
          }
          if (p.y < 0.02 || p.y > 0.98) {
            p.vy *= -1;
            p.y = Math.max(0.02, Math.min(0.98, p.y));
          }
          // Reseed velocity gently to keep things lively
          if (Math.abs(p.vx) < 0.001) p.vx = (Math.random() - 0.5) * 2;
          cx = pad + p.x * (w - 2 * pad);
          cy = pad + p.y * (h - 2 * pad);
        } else if (liqHere) {
          // Loose roaming around the bottom area, anchor drifts.
          const wobble = 0.35;
          p.phase += 2 * speedScale * dt * dtScale;
          cx = ax + Math.cos(p.phase + i) * cellW * wobble * speedScale;
          // Bias liquid downward (gravity-ish)
          const gy = pad + (rows - 0.5 - (i % rows) * 0.1) * cellH;
          cy = gy + Math.sin(p.phase * 1.3 + i) * cellH * wobble * speedScale;
          // Reset random walk fields so transition into gas is smooth
          p.x = (cx - pad) / (w - 2 * pad);
          p.y = (cy - pad) / (h - 2 * pad);
          p.vx = Math.cos(p.phase) * 1.2;
          p.vy = Math.sin(p.phase) * 1.2;
        } else {
          // Solid: vibrate around lattice anchor.
          p.phase += 6 * speedScale * dt * dtScale;
          const amp = 2 + 4 * speedScale;
          cx = ax + Math.cos(p.phase * 1.7 + i) * amp;
          cy = ay + Math.sin(p.phase * 2.1 + i * 0.7) * amp;
          p.x = (cx - pad) / (w - 2 * pad);
          p.y = (cy - pad) / (h - 2 * pad);
        }

        // Draw
        ctx.fillStyle = gasHere
          ? "hsl(210 80% 70%)"
          : liqHere
            ? "hsl(210 70% 55%)"
            : "hsl(210 60% 45%)";
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = window.requestAnimationFrame(tick);
    };
    animRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTimeRef.current = 0;
    };
  }, [info.phase, info.fraction, info.tempC, sub.boilC]);

  // Auto-play
  useEffect(() => {
    if (!playing) return;
    let raf: number;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setProgress((p) => {
        const next = p + dt * 0.07 * rateMult; // ~14s full sweep at 1×
        if (next >= 1) {
          setPlaying(false);
          return 1;
        }
        return next;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, rateMult]);

  // KE/PE bars (KE ∝ T(K); PE rises during phase changes and on warming)
  const tempK = info.tempC + 273.15;
  const maxK = sub.endC + 273.15;
  const ke = Math.max(0, Math.min(1, tempK / maxK));
  // PE: monotonically increasing with cumulative Q, normalized
  const pe = progress;

  const onDownload = () => {
    downloadPhaseLabPdf({
      substanceName: sub.name,
      formula: sub.formula,
      meltC: sub.meltC,
      boilC: sub.boilC,
      Lf: sub.Lf,
      Lv: sub.Lv,
      cs: sub.cs,
      cl: sub.cl,
      cg: sub.cg,
      progressPct: Math.round(progress * 100),
      qPerKg: progress * seg.total,
      totalQ: seg.total,
      tempC: info.tempC,
      phase: info.phase,
      kePct: Math.round(ke * 100),
      pePct: Math.round(pe * 100),
    });
  };

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Phase-Change &amp; Heating Curve Lab</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Scrub heat in. Watch the curve, the particles, and KE vs PE move together — the
              plateau finally makes sense.
            </p>
          </div>
          <button
            onClick={onDownload}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
          >
            <Download size={14} />
            Download worksheet
          </button>
        </div>

        {/* Substance */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider text-muted-foreground mr-2">
            Substance
          </span>
          {SUBSTANCES.map((s) => (
            <button
              key={s.key}
              onClick={() => {
                setSubKey(s.key);
                setProgress(0.05);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                subKey === s.key
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.name}
            </button>
          ))}
          <span className="text-xs text-muted-foreground ml-2 font-mono">
            mp {sub.meltC}°C · bp {sub.boilC}°C
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4">
          {/* Left col: curve + particle box */}
          <div className="space-y-4 min-w-0">
            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-foreground">Heating curve</h2>
                <span className="text-xs font-mono text-muted-foreground">
                  T = {info.tempC.toFixed(1)}°C · Q = {(progress * seg.total).toFixed(0)} kJ/kg
                </span>
              </div>
              <canvas ref={curveRef} className="w-full h-[200px] block" aria-label="Heating curve" />
            </div>

            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-foreground">Particle box</h2>
                <span className="text-xs font-medium text-primary capitalize">{info.phase}</span>
              </div>
              <div
                className="relative"
                onMouseEnter={() => setShowTip(true)}
                onMouseLeave={() => setShowTip(false)}
                onClick={() => setShowTip((s) => !s)}
              >
                <canvas
                  ref={boxRef}
                  className="w-full h-[280px] block cursor-help"
                  aria-label="Particle visualization. Hover or tap for KE/PE explanation."
                />
                {showTip && (
                  <div
                    role="tooltip"
                    className="absolute top-2 right-2 max-w-[260px] rounded-md border border-border bg-popover text-popover-foreground p-3 text-xs shadow-lg z-10"
                  >
                    <div className="font-semibold capitalize mb-1 text-foreground">
                      {info.phase}
                    </div>
                    <div className="mb-1">
                      <span className="text-amber-400 font-medium">KE:</span>{" "}
                      <span className="text-muted-foreground">{PHASE_TOOLTIP[info.phase].ke}</span>
                    </div>
                    <div>
                      <span className="text-primary font-medium">PE:</span>{" "}
                      <span className="text-muted-foreground">{PHASE_TOOLTIP[info.phase].pe}</span>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{PHASE_LABEL[info.phase]}</p>
            </div>

            {/* Heat-in slider + transport */}
            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <button
                  onClick={() => {
                    setProgress(0);
                    setPlaying(false);
                  }}
                  className="h-9 w-9 rounded-full bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center"
                  aria-label="Reset"
                >
                  <RotateCcw size={14} />
                </button>
                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={Math.round(progress * 1000)}
                  onChange={(e) => {
                    setProgress(Number(e.target.value) / 1000);
                  }}
                  className="flex-1 accent-primary"
                  aria-label="Heat added"
                />
                <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                  {Math.round(progress * 100)}%
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">
                  Heating rate
                </span>
                {RATE_OPTIONS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRateMult(r.mult)}
                    className={`px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${
                      rateMult === r.mult
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                    aria-pressed={rateMult === r.mult}
                  >
                    {r.label}
                  </button>
                ))}
                <span className="text-[11px] text-muted-foreground ml-1">
                  Affects play speed; particle motion stays physically consistent.
                </span>
              </div>
            </div>
          </div>

          {/* Right rail: KE/PE */}
          <div className="rounded-lg border border-border bg-tile-bg p-3">
            <h2 className="text-sm font-semibold text-foreground mb-3">Energy breakdown</h2>
            <div className="space-y-3">
              <Bar label="KE (∝ temperature)" value={ke} hue={45} />
              <Bar label="PE (intermolecular)" value={pe} hue={210} />
            </div>
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
              During <span className="text-foreground font-medium">melting</span> and{" "}
              <span className="text-foreground font-medium">boiling</span>, KE stays flat —
              that&apos;s why temperature plateaus. The added heat goes into PE: breaking
              attractions between particles.
            </p>
            <dl className="mt-4 space-y-1 text-xs font-mono text-muted-foreground">
              <Row k="ΔH fusion" v={`${sub.Lf} kJ/kg`} />
              <Row k="ΔH vap" v={`${sub.Lv} kJ/kg`} />
              <Row k="c (solid)" v={`${sub.cs} kJ/kg·K`} />
              <Row k="c (liquid)" v={`${sub.cl} kJ/kg·K`} />
              <Row k="c (gas)" v={`${sub.cg} kJ/kg·K`} />
            </dl>
          </div>
        </div>
      </Main>
    </div>
  );
}

function Bar({ label, value, hue }: { label: string; value: number; hue: number }) {
  const pct = Math.round(value * 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full transition-[width] duration-200"
          style={{ width: `${pct}%`, background: `hsl(${hue} 80% 55%)` }}
        />
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <dt>{k}</dt>
      <dd className="text-foreground">{v}</dd>
    </div>
  );
}